import { ref } from "vue";
import {
	HttpError,
	request,
	setAuthTokenProvider,
	setUnauthorizedRecovery,
} from "@/shared/http/client";
import { SUPPORT_KEY } from "@/shared/support-tab";

/**
 * Session state.
 *
 * The access token lives in a module-level ref — in MEMORY, never in
 * localStorage (F-ADR-008). The refresh token is an httpOnly cookie the browser
 * sends on its own and JavaScript cannot read.
 *
 * That pairing is load-bearing rather than decorative: this app renders
 * markdown, media and mermaid written by strangers, so a token reachable from
 * script would turn any XSS into a stolen session.
 */
export interface Identity {
	readonly userId: string;
	readonly tenantId: string;
	readonly role: "owner" | "admin" | "member";
}

const accessToken = ref<string | null>(null);
const identity = ref<Identity | null>(null);
const restoring = ref(true);

let refreshTimer: ReturnType<typeof setTimeout> | undefined;

export function useSessionState() {
	return { accessToken, identity, restoring };
}

/**
 * QUANDO A PERGUNTA "há sessão?" JÁ TEM RESPOSTA.
 *
 * ── O defeito ─────────────────────────────────────────────────────────────
 *
 * A moldura monta e as suas consultas partem no MESMO arranque em que o cookie
 * de refresh ainda está a ser trocado por um token. Resultado, a cada abertura
 * do painel: três 401 na consola (`/v3/structure/entities`, `/v3/workspace`,
 * `/v3/platform/access`), telas a repintar quando a repetição vinha boa, e um
 * erro vermelho indistinguível de um defeito real para quem abre a consola.
 *
 * ── Por que uma promessa e não a bandeira `restoring` ─────────────────────
 *
 * Porque `restoring` nunca fica falsa numa página pública ou do parceiro, onde
 * ninguém restaura coisa nenhuma — esperar por ela seria pendurar o pedido para
 * sempre. Isto resolve-se UMA vez, por quem sabe: o guarda do router, em todos
 * os seus caminhos (parceiro, pública, restaurada, já autenticada).
 */
let settle: () => void = () => undefined;
const settled = new Promise<void>((resolve) => {
	settle = resolve;
});

/** Idempotente: chamar duas vezes é o caso normal (o guarda corre a cada rota). */
export function markSessionSettled(): void {
	settle();
}

// Registered once, at module load: from here on the HTTP client attaches the
// token itself and no feature has to think about authentication.
//
// ESPERA a resposta quando ela ainda não existe. A própria chamada de refresh
// viaja com `auth: "none"`, então ela nunca espera por si mesma.
setAuthTokenProvider(async () => {
	if (accessToken.value) return accessToken.value;
	await settled;
	return accessToken.value;
});

/**
 * QUANDO O TOKEN DEIXA DE SERVIR — em relógio de parede, não em temporizador.
 *
 * O temporizador é uma intenção; isto é um facto. Um `setTimeout` de catorze
 * minutos numa aba em segundo plano é estrangulado pelo navegador, e não corre
 * de todo enquanto a máquina dorme. Guardar o INSTANTE permite perguntar "isto
 * ainda vale?" a quem volta ao ecrã, que é exatamente quem foi mal servido.
 */
let accessExpiresAt = 0;

/** Renews shortly before expiry so a request never races the clock. */
function scheduleRenewal(expiresInSeconds: number): void {
	if (refreshTimer) clearTimeout(refreshTimer);
	accessExpiresAt = Date.now() + expiresInSeconds * 1000;
	const leadSeconds = Math.min(60, Math.floor(expiresInSeconds / 3));
	refreshTimer = setTimeout(
		() => void refresh().catch(() => undefined),
		Math.max(5, expiresInSeconds - leadSeconds) * 1000,
	);
}

/**
 * Uma falha PASSAGEIRA não é o fim da sessão — e tratá-la como tal era o defeito.
 *
 * O `catch` desta renovação limpava o token e a identidade fosse qual fosse a
 * causa: uma rede a oscilar, um servidor a reiniciar, os 15s de tempo limite.
 * Pior, ninguém voltava a marcar relógio nenhum — a sessão ficava morta para
 * sempre com um cookie de refresh válido por mais vinte e nove dias. A próxima
 * navegação caía no ecrã de entrada, e a pessoa via "desligou-me outra vez".
 *
 * Meio minuto: perto o suficiente para recuperar antes de o acesso expirar,
 * longe o suficiente para não martelar um servidor que está a levantar-se.
 */
function scheduleRetry(): void {
	if (refreshTimer) clearTimeout(refreshTimer);
	refreshTimer = setTimeout(() => void refresh().catch(() => undefined), 30_000);
}

async function loadIdentity(): Promise<void> {
	identity.value = await request<Identity>("/v3/auth/me");
}

export async function signIn(email: string, password: string): Promise<void> {
	const res = await request<{ accessToken: string; expiresInSeconds: number }>("/v3/auth/sign-in", {
		method: "POST",
		body: { email, password },
	});
	// Entrar com senha encerra o modo suporte da aba: senão a próxima renovação
	// (ou o recarregar da troca de unidade) re-troca o link e volta ao cliente.
	sessionStorage.removeItem(SUPPORT_KEY);
	accessToken.value = res.accessToken;
	sessionEnded = false;
	scheduleRenewal(res.expiresInSeconds);
	await loadIdentity();
}

/**
 * UMA rotação de cada vez, e nunca duas.
 *
 * O servidor ROTACIONA: o token apresentado é revogado e sai um novo. Duas
 * chamadas concorrentes apresentam o mesmo e a segunda apanha um token já
 * revogado — que, do lado de lá, é a assinatura de um roubo, e a resposta a um
 * roubo é derrubar todas as sessões da pessoa. O servidor passou a tolerar essa
 * corrida numa janela curta; não a criar é a outra metade, e é esta.
 *
 * Também é o que faz um ecrã com seis consultas a levar 401 ao mesmo tempo
 * renovar UMA vez em vez de seis.
 */
let rotating: Promise<boolean> | null = null;

/**
 * A sessão acabou mesmo — dito pelo servidor, não deduzido de uma falha.
 *
 * Sem isto, cada pedido de uma tela morta tentaria renovar outra vez, e um
 * painel com dez consultas faria dez rotações contra um cookie que já não
 * existe. Volta a false ao entrar.
 */
let sessionEnded = false;

export async function refresh(): Promise<boolean> {
	rotating ??= rotate().finally(() => {
		rotating = null;
	});
	return rotating;
}

/**
 * O MODO SUPORTE: a sessão que nasce de um link, não de um cookie.
 *
 * O token do link fica em sessionStorage (morre com a aba — nunca localStorage,
 * F-ADR-008 vale dobrado para uma credencial de terceiros) e a renovação
 * RE-TROCA o link em vez de rodar o cookie: enquanto a concessão viver, a aba
 * segue; revogada/expirada, a troca leva 401 e a sessão acaba como qualquer
 * outra. Nada do fluxo normal muda — o desvio é uma guarda no rotate().
 */

export async function enterSupport(token: string): Promise<void> {
	sessionStorage.setItem(SUPPORT_KEY, token);
	const res = await request<{ accessToken: string; expiresInSeconds: number }>(
		"/v3/platform/support-access",
		{ method: "POST", body: { token }, auth: "none" },
	);
	accessToken.value = res.accessToken;
	sessionEnded = false;
	scheduleRenewal(res.expiresInSeconds);
	await loadIdentity();
}

async function rotateSupport(token: string): Promise<boolean> {
	try {
		const res = await request<{ accessToken: string; expiresInSeconds: number }>(
			"/v3/platform/support-access",
			{ method: "POST", body: { token }, auth: "none" },
		);
		accessToken.value = res.accessToken;
		sessionEnded = false;
		scheduleRenewal(res.expiresInSeconds);
		if (!identity.value) await loadIdentity();
		return true;
	} catch (e) {
		if (isUnauthorized(e)) {
			sessionStorage.removeItem(SUPPORT_KEY);
			sessionEnded = true;
			accessExpiresAt = 0;
			accessToken.value = null;
			identity.value = null;
			return false;
		}
		scheduleRetry();
		return false;
	}
}

/** O servidor disse que não há sessão: tudo o que a tela guardava dela sai. */
function endSession(): false {
	sessionEnded = true;
	accessExpiresAt = 0;
	accessToken.value = null;
	identity.value = null;
	return false;
}

async function rotate(): Promise<boolean> {
	const supportToken = sessionStorage.getItem(SUPPORT_KEY);
	if (supportToken) return rotateSupport(supportToken);
	try {
		const res = await request<{ accessToken: string; expiresInSeconds: number }>(
			"/v3/auth/refresh",
			// `"cookie"`, e não `"none"`: o que autentica esta chamada é o cookie
			// httpOnly — `"none"` passou a ir com `credentials: "omit"` (16/08) e a
			// renovação saía sem credencial NENHUMA: 401 NO_SESSION de quinze em
			// quinze minutos, que o painel lia como "a sessão acabou". Sem Bearer
			// na mesma: pedir o token aqui seria esperar pela chamada que o produz.
			{ method: "POST", auth: "cookie" },
		);
		// 204 sem corpo: não havia cookie — ninguém entrou. A mesma resposta que
		// um 401, sem o erro vermelho no console de quem só abriu o login.
		if (!res?.accessToken) return endSession();
		accessToken.value = res.accessToken;
		sessionEnded = false;
		scheduleRenewal(res.expiresInSeconds);
		if (!identity.value) await loadIdentity();
		return true;
	} catch (e) {
		// A ÚNICA resposta que encerra uma sessão é o servidor dizer que ela acabou.
		// Uma rede em baixo diz apenas que ninguém sabe.
		if (isUnauthorized(e)) return endSession();
		scheduleRetry();
		return false;
	}
}

/**
 * O 401 que se recupera sozinho.
 *
 * Registado no arranque, chamado pelo cliente HTTP quando um pedido leva 401: se
 * a renovação correr bem, o pedido é repetido uma vez e quem o fez nunca soube
 * de nada. É a rede de segurança por baixo do relógio — e o relógio falha por
 * razões banais (aba estrangulada, máquina a dormir, servidor a reiniciar).
 */
setUnauthorizedRecovery(async () => {
	if (sessionEnded) return false;
	return refresh();
});

/**
 * De volta ao ecrã: o token ainda vale?
 *
 * Quem esteve uma hora noutra aba tem um temporizador que o navegador
 * estrangulou e um token que expirou há quarenta minutos. Perguntar pelo relógio
 * de parede no momento em que a aba volta a ficar visível é o que transforma
 * "voltei e tinha sido desligado" em "voltei e estava tudo lá".
 */
if (typeof document !== "undefined") {
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState !== "visible") return;
		if (sessionEnded || !identity.value) return;
		// Um minuto de folga: o pedido que a pessoa está prestes a fazer não deve
		// apanhar o token no último suspiro.
		if (Date.now() < accessExpiresAt - 60_000) return;
		void refresh().catch(() => undefined);
	});
}

export async function signOut(): Promise<void> {
	try {
		await request("/v3/auth/sign-out", { method: "POST" });
	} catch {
		// Signing out locally must succeed even if the server call does not.
	}
	if (refreshTimer) clearTimeout(refreshTimer);
	sessionStorage.removeItem(SUPPORT_KEY);
	// Sair é uma decisão, e vale como resposta do servidor: nada aqui deve tentar
	// recuperar a sessão que a pessoa acabou de fechar.
	sessionEnded = true;
	accessExpiresAt = 0;
	accessToken.value = null;
	identity.value = null;
}

/**
 * On boot, try the refresh cookie once. A reload should not cost a password, and
 * a failure here is the normal "not signed in" case rather than an error.
 *
 * ── E "UMA VEZ" NÃO CHEGAVA, no arranque ──────────────────────────────────
 *
 * `rotate()` já distingue as duas respostas: o servidor dizer 401 ENCERRA a
 * sessão; qualquer outra falha diz apenas que ninguém sabe, e deixa o cookie e o
 * relógio de repetição de pé. Só que quem espera por esta função é a guarda do
 * router, e para ela as duas eram a mesma: `identity` vazia manda para o ecrã de
 * entrada. Um soluço de rede, um `/auth/me` a demorar, o servidor a levantar-se
 * — e a pessoa recarregava uma página e caía no login com um cookie válido por
 * mais vinte e nove dias. Visto uma vez num run da suíte (06/09): a rotação
 * seguinte, dois segundos depois, correu bem; o painel já estava no `/sign-in`.
 *
 * A segunda tentativa é imediata de propósito: quem está a olhar para um ecrã
 * branco não espera trinta segundos pelo `scheduleRetry`. E é só a segunda —
 * depois dela, a resposta "não há sessão" é boa o suficiente para mandar
 * alguém entrar.
 */
export async function restoreSession(): Promise<void> {
	restoring.value = true;
	try {
		if (!(await refresh()) && !sessionEnded) await refresh();
	} finally {
		restoring.value = false;
		// Haja sessão ou não, a pergunta tem resposta — e quem estava à espera do
		// token pode seguir com ela.
		markSessionSettled();
	}
}

export function isUnauthorized(e: unknown): boolean {
	return e instanceof HttpError && e.kind === "unauthorized";
}

/**
 * Creating a workspace, for someone who has no account at all.
 *
 * Deliberately does NOT issue a session. Signing in afterwards goes through the
 * one code path that checks the password, records the attempt and mints the
 * token — a second path that hands out a session would be a second place for
 * that to go wrong.
 */
export interface NewWorkspace {
	readonly tenantId: string;
	readonly slug: string;
	readonly email: string;
	/** A paid plan from the link: the account starts on the trial, the checkout comes next. */
	readonly checkoutPlanCode?: string;
}

/**
 * Which terms the sign-up screen shows, and which version it records.
 *
 * ONE declaration, read by both `/terms` and the sign-up body. Two copies drift
 * the day the text changes, and then the acceptance on file points at a text
 * nobody ever read — which is the only thing this record exists to prevent.
 *
 * Bump it whenever the text at `/terms` changes in substance.
 */
export const TERMS_VERSION = "2026-09-25";

export async function signUpAvailable(): Promise<boolean> {
	try {
		const res = await request<{ open: boolean }>("/v3/auth/sign-up");
		return res.open;
	} catch {
		// A closed or unreachable installation shows no button. Offering one that
		// fails is worse than offering none.
		return false;
	}
}

/**
 * What the door says about a registration code, before the form is filled.
 *
 * Same GET as `signUpAvailable`, with the code alongside — ONE route, not a
 * second contract. The server answers identically for an invalid code and a
 * nonexistent one, so this cannot be used to enumerate which codes exist:
 * the screen either says "applied" or stays neutral.
 */
export interface SignUpGate {
	readonly open: boolean;
	readonly codeValid: boolean;
	/** Present when the code carries a plan (`PARTNER-plan`), so the screen can name it. */
	readonly planName?: string;
	/** The plan above is paid: payment right after the account is created. */
	readonly planPaid?: boolean;
	/** The plan the code named that this partner no longer sells: the account starts on the trial. */
	readonly unavailablePlanName?: string;
}

/** The checkout of the paid plan a sign-up link named — the same door the billing grid uses. */
export async function startCheckout(planCode: string): Promise<string | null> {
	const res = await request<{ checkoutUrl: string | null }>("/v3/billing/plan", {
		method: "POST",
		body: { planCode, interval: "month" },
	});
	return res.checkoutUrl;
}

/**
 * Stripe returns every checkout to `/cobranca`. One that left from the sign-up
 * belongs back in the onboarding: the tab carries that note across the round trip.
 * Storage can throw (private window); then the person just lands on billing.
 */
const AFTER_SIGNUP_CHECKOUT = "benvox.checkout-from-signup";

export function markCheckoutFromSignUp(): void {
	try {
		sessionStorage.setItem(AFTER_SIGNUP_CHECKOUT, "1");
	} catch {}
}

/** True once, on the return from a sign-up checkout. */
export function takeCheckoutFromSignUp(): boolean {
	try {
		const marked = sessionStorage.getItem(AFTER_SIGNUP_CHECKOUT) === "1";
		sessionStorage.removeItem(AFTER_SIGNUP_CHECKOUT);
		return marked;
	} catch {
		return false;
	}
}

export async function checkSignUpCode(code: string): Promise<SignUpGate> {
	const res = await request<{
		open: boolean;
		codeValid?: boolean;
		planName?: string;
		planPaid?: boolean;
		unavailablePlanName?: string;
	}>(`/v3/auth/sign-up?code=${encodeURIComponent(code)}`);
	return {
		open: res.open,
		codeValid: res.codeValid === true,
		planName: res.planName,
		planPaid: res.planPaid === true,
		unavailablePlanName: res.unavailablePlanName,
	};
}

export async function signUp(input: {
	onboarding: { newBusiness: boolean; businessName: string };
	/**
	 * The registration code — REQUIRED at the public door. Every V2 customer has
	 * an origin, and V3 keeps that: the code names the partner who referred this
	 * workspace, optionally with a plan suffix. The server resolves it; this
	 * screen only carries it.
	 */
	code: string;
	workspaceName: string;
	ownerName: string;
	email: string;
	password: string;
	/**
	 * Two letters, and far from cosmetic: the server derives the timezone and the
	 * currency from it, and the validator for a national document is resolved by
	 * country (ADR-018). Omitting it left every workspace as Brazil by default.
	 */
	country?: string;
	language?: string;
	/**
	 * The version of the text that was on screen when the box was ticked. Only the
	 * client knows that; the server stamps the date, and refuses one sent from
	 * here — a date of acceptance chosen by whoever accepted proves nothing.
	 */
	acceptedTermsVersion: string;
}): Promise<NewWorkspace> {
	const res = await request<{ workspace: NewWorkspace }>("/v3/auth/sign-up", {
		method: "POST",
		body: input,
		// O cadastro instala o sistema do parceiro antes de responder (10–20 s num de 19
		// telas): os 15 s de sempre abortavam a espera e a pessoa ficava no formulário
		// com a conta já criada (E2E restaurante, 01/11).
		timeoutMs: 120_000,
	});
	return res.workspace;
}
